// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Truck, CheckCircle2, XCircle, RefreshCw, Snowflake, PenLine, Tag } from 'lucide-react'
import * as api from '../../api/client'
import { createPortal } from 'react-dom'
import ShippingLabel, { type LabelData } from '../pharmacy/ShippingLabel'
import SyncCourierOrders from './SyncCourierOrders'
import { carrierLabel, carrierSetup } from '../../lib/carriers'
import PickupLocationField, { PickupLocationName } from '../../components/shipping/PickupLocationField'

/**
 * Settings → Shipping tab (Sprint 18). Manage carrier accounts: view config,
 * test the live connection, toggle defaults. Gated by shipping.config.
 *
 * Compliance: credentials are never shown — only has_credentials + a masked
 * status. The Test button performs a live carrier auth and shows the result.
 */
export default function ShippingSettingsTab() {
  const [carriers, setCarriers] = useState<api.ShippingCarrier[]>([])
  const [supported, setSupported] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [testing, setTesting] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; detail: string }>>({})

  const load = async () => {
    setLoading(true); setError(null)
    try {
      const res = await api.listShippingCarriers()
      setCarriers(res.items)
      setSupported(res.supported_carriers)
    } catch (e: any) {
      setError(e?.message || 'Could not load carriers.')
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  // ASP-89b/91 follow-up (Patrick 2026-07-28: "No edit option"): inline edit form
  // per carrier card, backed by the existing PATCH (partial update). Credentials
  // are deliberately NOT editable here — they keep the masked stored/missing flow.
  const [editId, setEditId] = useState<string | null>(null)
  const [edit, setEdit] = useState<{ display_name: string; account_number: string; base_url: string; api_website: string; is_test_mode: boolean; default_cold_chain: boolean; default_signature: boolean; require_quote_confirm: boolean; send_cod: boolean; is_active: boolean }>({ display_name: '', account_number: '', base_url: '', api_website: '', is_test_mode: true, default_cold_chain: false, default_signature: false, require_quote_confirm: true, send_cod: false, is_active: true })
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const startEdit = (c: api.ShippingCarrier) => {
    setEditId(c.id); setSaveErr(null)
    setEdit({
      display_name: c.display_name, account_number: c.account_number ?? '', base_url: c.base_url ?? '',
      api_website: c.api_website ?? '', is_test_mode: c.is_test_mode, default_cold_chain: c.default_cold_chain,
      default_signature: c.default_signature, require_quote_confirm: c.require_quote_confirm !== false,
      send_cod: c.send_cod === true, is_active: c.is_active,
    })
  }
  const saveEdit = async () => {
    if (!editId) return
    if (!edit.display_name.trim()) { setSaveErr('Display name is required.'); return }
    setSaving(true); setSaveErr(null)
    try {
      await api.updateShippingCarrier(editId, {
        display_name: edit.display_name.trim(),
        account_number: edit.account_number.trim() || undefined,
        base_url: edit.base_url.trim() || undefined,
        api_website: edit.api_website.trim() || undefined,
        is_test_mode: edit.is_test_mode,
        default_cold_chain: edit.default_cold_chain,
        default_signature: edit.default_signature,
        require_quote_confirm: edit.require_quote_confirm,
        send_cod: edit.send_cod,
        is_active: edit.is_active,
      })
      setEditId(null)
      await load()
    } catch (e: any) { setSaveErr(e?.message || 'Save failed.') } finally { setSaving(false) }
  }

  const test = async (id: string) => {
    setTesting(id)
    try {
      const r = await api.testShippingCarrier(id)
      setTestResult(prev => ({ ...prev, [id]: { ok: r.ok, detail: r.detail || (r.ok ? 'Connected' : 'Failed') } }))
      load()  // refresh last_verified_at
    } catch (e: any) {
      setTestResult(prev => ({ ...prev, [id]: { ok: false, detail: e?.message || 'Test failed' } }))
    } finally { setTesting(null) }
  }

  if (loading) return <div style={mutedText}>Loading carriers…</div>

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Truck size={18} />
        <h2 style={{ margin: 0, fontSize: 16 }}>Shipping carriers</h2>
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        Carrier accounts used to ship prescriptions. Supported: {supported.map(carrierLabel).join(', ')}.
      </p>

      {error && <div style={errorBox}>{error}</div>}

      {carriers.length === 0 ? (
        <div style={emptyBox}>No carrier accounts configured.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {carriers.map(c => {
            const tr = testResult[c.id]
            return (
              <div key={c.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 14 }}>{c.display_name}</strong>
                  <span style={tag}>{carrierLabel(c.carrier)}</span>
                  {c.is_test_mode && <span style={{ ...tag, background: '#a162070d', color: '#a16207', borderColor: '#a1620733' }}>Test mode</span>}
                  {!c.is_active && <span style={{ ...tag, color: '#6b7280' }}>Inactive</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', margin: '10px 0', fontSize: 13 }}>
                  {carrierSetup(c.carrier).pickupFromCarrier
                    ? <Row label="Branch"><PickupLocationName carrierId={c.id} id={c.account_number} /></Row>
                    : <Row label="Account">{c.account_number || '—'}</Row>}
                  <Row label="API site">{c.api_website || '—'}</Row>
                  <Row label="Base URL">{c.base_url ? <span style={{ fontSize: 12 }}>{c.base_url}</span> : <span style={{ color: '#a16207' }}>not set</span>}</Row>
                  <Row label="Credentials">{c.has_credentials ? <span style={{ color: '#15803d' }}>stored</span> : <span style={{ color: '#dc2626' }}>missing</span>}{c.credentials_unencrypted && <span style={{ color: '#a16207', marginLeft: 6, fontSize: 11 }}>(plaintext — save to encrypt)</span>}</Row>
                  <Row label="Default handling">
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      {c.default_cold_chain && <span style={badge}><Snowflake size={10} /> Cold chain</span>}
                      {c.default_signature && <span style={badge}><PenLine size={10} /> Signature</span>}
                      {!c.default_cold_chain && !c.default_signature && '—'}
                    </span>
                  </Row>
                  <Row label="Quote before buy">{c.require_quote_confirm === false ? 'No (auto-ship)' : 'Yes (confirm rate)'}</Row>
                  <Row label="COD to courier">{c.send_cod === true ? 'On (driver collects copay)' : 'Off (label only)'}</Row>
                  <Row label="Last verified">{c.last_verified_at ? new Date(c.last_verified_at).toLocaleString() : 'never'}</Row>
                </div>

                {c.last_verify_error && !tr && (
                  <div style={{ ...errorBox, marginBottom: 8 }}>Last error: {c.last_verify_error}</div>
                )}
                {tr && (
                  <div style={tr.ok ? okBox : errorBox}>
                    {tr.ok ? <CheckCircle2 size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <XCircle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                    {tr.detail}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => test(c.id)} disabled={testing === c.id} style={primaryBtn}>
                    <RefreshCw size={13} style={{ animation: testing === c.id ? 'spin 1s linear infinite' : undefined }} />
                    {testing === c.id ? ' Testing…' : ' Test connection'}
                  </button>
                  <button type="button" onClick={() => editId === c.id ? setEditId(null) : startEdit(c)}
                    style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                    {editId === c.id ? 'Cancel' : '✎ Edit'}
                  </button>
                </div>

                {editId === c.id && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-bg-subtle, #f9fafb)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', fontSize: 13 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>Display name</span>
                        <input value={edit.display_name} onChange={e => setEdit(v => ({ ...v, display_name: e.target.value }))} maxLength={80}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 13 }} />
                      </label>
                      {carrierSetup(c.carrier).pickupFromCarrier
                        ? <PickupLocationField carrierId={c.id} label="Default branch" value={edit.account_number}
                            onChange={id => setEdit(v => ({ ...v, account_number: id }))} />
                        : <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>Account number</span>
                            <input value={edit.account_number} onChange={e => setEdit(v => ({ ...v, account_number: e.target.value }))} maxLength={64}
                              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 13 }} />
                          </label>}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>Base URL</span>
                        <input value={edit.base_url} onChange={e => setEdit(v => ({ ...v, base_url: e.target.value }))} maxLength={256}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>API site</span>
                        <input value={edit.api_website} onChange={e => setEdit(v => ({ ...v, api_website: e.target.value }))} maxLength={128}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 13 }} />
                      </label>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 10, fontSize: 13 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={edit.is_test_mode} onChange={e => setEdit(v => ({ ...v, is_test_mode: e.target.checked }))} /> Test mode
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={edit.default_cold_chain} onChange={e => setEdit(v => ({ ...v, default_cold_chain: e.target.checked }))} /> Default cold chain
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={edit.default_signature} onChange={e => setEdit(v => ({ ...v, default_signature: e.target.checked }))} /> Default signature
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={edit.require_quote_confirm} onChange={e => setEdit(v => ({ ...v, require_quote_confirm: e.target.checked }))} /> Quote before buy
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={edit.is_active} onChange={e => setEdit(v => ({ ...v, is_active: e.target.checked }))} /> Active
                      </label>
                    </div>
                    {/* D-SHIP-COD. Given its own row, away from the handling defaults, because it is
                        not a preference — it is the switch that starts money being demanded at
                        patients' doors on the strength of our copay engine. The warning is shown
                        while it is ON, where it is relevant, not as permanent noise. */}
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer', marginTop: 10 }}>
                      <input type="checkbox" checked={edit.send_cod} style={{ marginTop: 3 }}
                        onChange={e => setEdit(v => ({ ...v, send_cod: e.target.checked }))} />
                      <span>
                        Send copay to courier as COD
                        <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                          The driver is asked to collect the amount printed on the label. Off means the
                          courier is told nothing and collection relies on the printed label alone.
                        </div>
                        {edit.send_cod && (
                          <div style={{ fontSize: 11.5, color: '#9a3412', marginTop: 4 }}>
                            Every new order will carry a COD amount. Turning this off takes effect on the
                            next order.
                          </div>
                        )}
                      </span>
                    </label>
                    {saveErr && <div style={{ ...errorBox, marginTop: 10 }}>{saveErr}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button type="button" onClick={() => void saveEdit()} disabled={saving} style={primaryBtn}>
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button type="button" onClick={() => setEditId(null)}
                        style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <LabelConfigPanel />

      <PioneerWritebackSection />
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>Courier account sync</h3>
        <SyncCourierOrders />
      </div>
    </div>
  )
}

// ── PioneerRx write-back (D-SHIP-2 staging) ──────────────────────────────────
// Connection settings for delivery → PioneerRx write-back (POS Universal API).
// Credentials are stored encrypted server-side and never echoed back; the
// write-back ENGINE ships with D-SHIP-2 — this stages the pharmacy's test env.
function PioneerWritebackSection() {
  const [cfg, setCfg] = useState<api.PioneerWriteback | null>(null)
  const [baseUrl, setBaseUrl] = useState('')
  const [apiType, setApiType] = useState<'enterprise' | 'pos_universal'>('enterprise')
  const [vendorKey, setVendorKey] = useState('')
  const [dataExchangeId, setDataExchangeId] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [sharedSecret, setSharedSecret] = useState('')
  const [certThumb, setCertThumb] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [testMode, setTestMode] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testingPx, setTestingPx] = useState(false)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    api.getPioneerWriteback().then(c => {
      setCfg(c); setBaseUrl(c.base_url ?? ''); setEnabled(c.enabled); setTestMode(c.is_test_mode)
      setApiType(c.api_type === 'pos_universal' ? 'pos_universal' : 'enterprise'); setCertThumb(c.cert_thumbprint ?? '')
    }).catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true); setNote(null)
    try {
      const res = await api.putPioneerWriteback({
        enabled, is_test_mode: testMode, base_url: baseUrl.trim(), api_type: apiType,
        cert_thumbprint: certThumb.trim() || undefined,
        ...(apiType === 'pos_universal' && vendorKey.trim() && dataExchangeId.trim()
          ? { vendor_key: vendorKey.trim(), data_exchange_id: dataExchangeId.trim() } : {}),
        ...(apiType === 'enterprise' && employeeId.trim() && sharedSecret.trim()
          ? { employee_id: employeeId.trim(), shared_secret: sharedSecret.trim() } : {}),
      })
      setNote({ ok: true, text: res.credentials_rotated ? 'Saved — credentials updated.' : 'Saved.' })
      setVendorKey(''); setDataExchangeId(''); setEmployeeId(''); setSharedSecret('')
      const c = await api.getPioneerWriteback(); setCfg(c)
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Save failed' })
    } finally { setSaving(false) }
  }

  const runTest = async () => {
    setTestingPx(true); setNote(null)
    try {
      const r = await api.testPioneerWriteback()
      const extra = r.server_cert_thumbprint && !certThumb ? ` Server cert SHA-256: ${r.server_cert_thumbprint} — paste it into the thumbprint field and Save to pin it.` : ''
      setNote({ ok: r.status === 'connected' || r.status === 'connected_bad_request', text: r.message + extra })
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Test failed' })
    } finally { setTestingPx(false) }
  }

  return (
    <div style={{ marginTop: 28 }}>
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>PioneerRx write-back</h3>
      <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 0 }}>
        Connection to this pharmacy's PioneerRx server (POS Universal API) for delivery write-back —
        delivered status, signature, and picked-up-by will post to PioneerRx when the write-back engine ships.
        Credentials are stored encrypted and never displayed.
      </p>
      <div style={{ maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 12 }}>Base URL (the pharmacy's PioneerRx server, https required)
          <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://pioneer.yourpharmacy.local"
            style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
        </label>
        <label style={{ fontSize: 12 }}>API type
          <select value={apiType} onChange={e => setApiType(e.target.value as 'enterprise' | 'pos_universal')}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }}>
            <option value="enterprise">Enterprise API (shared secret + employee ID)</option>
            <option value="pos_universal">POS Universal (VendorKey + DataExchangeID)</option>
          </select>
        </label>
        {apiType === 'enterprise' ? (<>
          <label style={{ fontSize: 12 }}>Employee ID (RequestedByEmployeeID) {cfg?.has_credentials && <span style={{ color: 'var(--color-text-muted)' }}>(on file — enter to replace)</span>}
            <input value={employeeId} onChange={e => setEmployeeId(e.target.value)} placeholder={cfg?.has_credentials ? '•••••' : 'e.g. dhart'}
              autoComplete="off"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 12 }}>Shared secret
            <input type="password" value={sharedSecret} onChange={e => setSharedSecret(e.target.value)} placeholder={cfg?.has_credentials ? '••••••••' : ''}
              autoComplete="new-password"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 12 }}>Server certificate thumbprint (SHA-256, optional — pins the self-signed cert)
            <input value={certThumb} onChange={e => setCertThumb(e.target.value)} placeholder="run Test once to discover it"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
          </label>
        </>) : (<>
          <label style={{ fontSize: 12 }}>Vendor Key {cfg?.has_credentials && <span style={{ color: 'var(--color-text-muted)' }}>(on file — enter to replace)</span>}
            <input type="password" value={vendorKey} onChange={e => setVendorKey(e.target.value)} placeholder={cfg?.has_credentials ? '••••••••' : ''}
              autoComplete="new-password"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
          </label>
          <label style={{ fontSize: 12 }}>Data Exchange ID {cfg?.has_credentials && <span style={{ color: 'var(--color-text-muted)' }}>(on file — enter to replace)</span>}
            <input type="password" value={dataExchangeId} onChange={e => setDataExchangeId(e.target.value)} placeholder={cfg?.has_credentials ? '••••••••' : ''}
              autoComplete="new-password"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, marginTop: 2 }} />
          </label>
        </>)}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={testMode} onChange={e => setTestMode(e.target.checked)} />
          Test mode (points at the PioneerRx test environment)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enable write-back (takes effect when the write-back engine ships)
        </label>
        {note && (
          <div style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 6,
            background: note.ok ? '#f0fdf4' : '#fef2f2', color: note.ok ? '#166534' : '#991b1b',
            border: `1px solid ${note.ok ? '#bbf7d0' : '#fecaca'}` }}>
            {note.text}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={save} disabled={saving}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary, #4338ca)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={runTest} disabled={testingPx || (!cfg?.has_credentials && !(vendorKey && dataExchangeId) && !(employeeId && sharedSecret))}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13 }}>
            {testingPx ? 'Testing…' : 'Test connection'}
          </button>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
          Save first, then Test. Enterprise probes {'{base URL}'}/api/enterprise/method/test/process with the signature auth; POS Universal probes /api/POS/RxQuery.
        </div>
      </div>
    </div>
  )
}

// ── Label customization panel ────────────────────────────────────────────────
function LabelConfigPanel() {
  const [cfg, setCfg] = useState<api.LabelConfig | null>(null)
  const [pharmacyLogo, setPharmacyLogo] = useState<string | null>(null)
  const [pharmacyName, setPharmacyName] = useState('Your Pharmacy')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // D-SHIP-8 test-label mock state — MUST live above the early return below:
  // hooks after a conditional return change the hook count between renders
  // (Rules of Hooks violation -> React #310 -> blank page). Caught live 2026-07-31.
  const [mockRxCount, setMockRxCount] = useState(3)
  const [mockCopay, setMockCopay] = useState(true)
  const [testPrint, setTestPrint] = useState(false)

  useEffect(() => {
    api.getLabelConfig().then(res => {
      setCfg(res.config)
      setPharmacyLogo(res.pharmacy_logo_url)
      if (res.pharmacy_name) setPharmacyName(res.pharmacy_name)
    }).catch(() => setErr('Could not load label settings.'))
  }, [])

  if (!cfg) return null
  const up = (patch: Partial<api.LabelConfig>) => { setCfg({ ...cfg, ...patch }); setSaved(false) }

  const onCustomLogo = (file: File) => {
    if (file.size > 1_000_000) { setErr('Logo must be under 1MB.'); return }
    const reader = new FileReader()
    reader.onload = () => up({ custom_logo_url: reader.result as string, logo_mode: 'custom' })
    reader.readAsDataURL(file)
  }

  const save = async () => {
    setSaving(true); setErr(null)
    try { await api.putLabelConfig(cfg); setSaved(true) }
    catch (e: any) { setErr(e?.payload?.message || e?.message || 'Save failed.') }
    finally { setSaving(false) }
  }

  // D-SHIP-8 (Patrick): test-label mock controls — pick how many Rx to include and
  // whether a mock copay/COD receipt is present, so the auto font-fit can be seen
  // reacting live (sparse label grows text; busy multi-Rx + receipt shrinks it).
  // The preview and Print test label BOTH use the CURRENT UNSAVED edits (cfg).
  const mockRxNumbers = Array.from({ length: mockRxCount }, (_, i) => `RX-44718${(82 + i).toString().padStart(2, '0')}-${i % 3}`)
  const mockAmounts = [25, 12.5, 0, 8.75, 40, 5, 0, 15.25]
  const mockCopayItems = mockCopay
    ? mockRxNumbers.map((rx, i) => ({ rx_number: rx, copay: mockAmounts[i % mockAmounts.length] }))
    : undefined
  const mockCopayTotal = mockCopayItems ? mockCopayItems.reduce((a, i) => a + i.copay, 0) : null
  const sample: LabelData = {
    carrier: 'roadpas', trackingNumber: 'RP 2073 0418 7749', orderRef: '2073',
    pharmacyName,
    pharmacyLogoUrl: cfg.logo_mode === 'custom' ? cfg.custom_logo_url : cfg.logo_mode === 'pharmacy' ? pharmacyLogo : null,
    fromLine: '1247 Beverly Blvd, Los Angeles, CA 90026 · (323) 555-0100',
    recipientName: 'Maria Hernandez', toLine1: '4820 Whittier Blvd',
    toCityStateZip: 'Los Angeles, CA 90022', toPhone: '(323) 555-0188',
    coldChain: true, signature: true, adultSignature: false, saturday: false,
    instructions: 'Hand to patient only. Keep refrigerated 2–8°C.',
    rxNumber: mockRxNumbers[0], rxNumbers: mockRxNumbers, isManual: false,
    serviceLevel: 'NEXTDAY',
    shipDate: new Date().toLocaleDateString('en-US'),
    copay: mockCopayTotal,
    copayItems: mockCopayItems ?? null,
  }
  const pageSizeMap: Record<string, string> = { '4x6': '100mm 150mm', '4x3': '100mm 76mm', letter: '8.5in 11in' }   // matches LabelPrintView PAGE_SIZE
  const testPageSize = pageSizeMap[cfg.size] ?? '100mm 150mm' 

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Tag size={18} />
        <h2 style={{ margin: 0, fontSize: 16 }}>Shipping label</h2>
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 0 }}>
        Customize the printed label. The barcode and QR code are always included so the
        courier can scan it. Medication name is never printed (PHI).
      </p>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {/* controls */}
        <div style={{ flex: '1 1 300px', minWidth: 280 }}>
          <label style={labelStyle}>Label size (printer)</label>
          <select value={cfg.size} onChange={e => up({ size: e.target.value as any })} style={inputStyle}>
            <option value="4x6">4 × 6 in (thermal — most common)</option>
            <option value="4x3">4 × 3 in (compact thermal)</option>
            <option value="letter">8.5 × 11 in (laser / full sheet)</option>
          </select>

          <label style={labelStyle}>Logo</label>
          <select value={cfg.logo_mode} onChange={e => up({ logo_mode: e.target.value as any })} style={inputStyle}>
            <option value="pharmacy">Use my pharmacy logo</option>
            <option value="custom">Upload a custom label logo</option>
            <option value="none">No logo</option>
          </select>
          {cfg.logo_mode === 'custom' && (
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ marginTop: 6, fontSize: 12 }}
              onChange={e => e.target.files?.[0] && onCustomLogo(e.target.files[0])} />
          )}

          <label style={labelStyle}>Barcode / QR encodes</label>
          <select value={cfg.barcode_value} onChange={e => up({ barcode_value: e.target.value as any })} style={inputStyle}>
            <option value="tracking">Tracking number</option>
            <option value="order">Carrier order number</option>
          </select>
          <div style={{ fontSize: 11, color: '#a16207', marginTop: 4 }}>
            Confirm with your courier (e.g. Roadpas) which value their scanner expects before go-live.
          </div>

          <label style={labelStyle}>Accent color</label>
          <input type="color" value={cfg.accent_hex} onChange={e => up({ accent_hex: e.target.value })} style={{ width: 48, height: 30, border: '1px solid var(--color-border)', borderRadius: 6 }} />

          <label style={labelStyle}>Base font size</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={cfg.base_font_px ?? 14} onChange={e => up({ base_font_px: Number(e.target.value) })}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
              {[10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40].map(n => (
                <option key={n} value={n}>{n}px{n === 14 ? ' (default)' : ''}</option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>
              Auto-fit is always on: text grows to fill spare room and shrinks so everything stays on one label.
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            <Chk checked={cfg.show_handling_flags} onChange={v => up({ show_handling_flags: v })} label="Show handling flags (cold-chain, signature)" />
            <Chk checked={cfg.show_instructions} onChange={v => up({ show_instructions: v })} label="Show special instructions" />
            <Chk checked={cfg.show_from_block} onChange={v => up({ show_from_block: v })} label="Show return (from) address" />
            <Chk checked={cfg.show_copay} onChange={v => up({ show_copay: v })} label="Show patient copay (if known)" />
            {cfg.show_copay && (
              <div style={{ fontSize: 11, color: '#a16207', marginLeft: 22, marginTop: -2, lineHeight: 1.4 }}>
                ⚠ Printing the copay on the outside of the package may reveal that a high-value medication
                is inside. Consider whether your delivery process and locations can support this safely.
                Only rendered when the Rx is in OwlRx and copay &gt; $0.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            <Chk checked={cfg.show_refrigerate_banner} onChange={v => up({ show_refrigerate_banner: v })} label="Refrigerate banner at top of label (cold-chain shipments)" />
            <Chk checked={cfg.show_consult_message} onChange={v => up({ show_consult_message: v })} label="Print pharmacist consultation message" />
            <Chk checked={cfg.show_sort_qr} onChange={v => up({ show_sort_qr: v })} label="Sorter aids: destination ZIP + order QR next to service type" />
            {cfg.show_consult_message && (
              <>
                <textarea
                  value={cfg.consult_message ?? ''}
                  onChange={e => up({ consult_message: e.target.value })}
                  maxLength={600}
                  rows={3}
                  placeholder="Questions About Your Medication? A licensed pharmacist is available for consultation. Please call 323-999-2775 during business hours to request a consultation or ask any questions about your medication."
                  style={{ marginLeft: 22, marginTop: 2, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, lineHeight: 1.4, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginLeft: 22, marginTop: -2 }}>
                  Printed at the bottom of every label. Include your pharmacy's consultation phone number.
                </div>
              </>
            )}
          </div>

          {/* ASP-88: quick-text presets for the create-shipment drawer's special
              instructions (checkbox multi-select; text stays editable). */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Quick-text special instructions</div>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 8 }}>
              These appear as checkboxes when creating a shipment; checked items are added to the special
              instructions the courier sees. One per line, up to 20.
            </div>
            <textarea
              value={(cfg.instruction_presets ?? []).join('\n')}
              onChange={e => up({ instruction_presets: e.target.value.split('\n') })}
              rows={4}
              placeholder={'Okay to porch\nCall patient upon arrival\nPlease text patient'}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit', width: '100%', maxWidth: 480 }}
            />
          </div>

          {/* Custom label elements: free text placed in header / body / footer */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Custom label text</div>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 8 }}>
              Add your own text to the label and choose where it prints. Banner style prints as a full-width color strip.
            </div>
            {(cfg.custom_elements ?? []).map((el, i) => {
              const els = cfg.custom_elements ?? []
              const set = (patch: Partial<typeof el>) => up({ custom_elements: els.map((e, j) => j === i ? { ...e, ...patch } : e) })
              const move = (d: number) => {
                const to = i + d; if (to < 0 || to >= els.length) return
                const next = [...els]; const [x] = next.splice(i, 1); next.splice(to, 0, x)
                up({ custom_elements: next })
              }
              return (
                <div key={el.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
                  <textarea value={el.text} maxLength={300} rows={2}
                    onChange={e => set({ text: e.target.value })}
                    placeholder="Text to print on the label"
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, lineHeight: 1.35, resize: 'vertical', fontFamily: 'inherit' }} />
                  <select value={el.region} onChange={e => set({ region: e.target.value as any })}
                    style={{ padding: '6px 6px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
                    <option value="header">Header</option>
                    <option value="body">Body</option>
                    <option value="footer">Footer</option>
                  </select>
                  <select value={el.emphasis} onChange={e => set({ emphasis: e.target.value as any })}
                    style={{ padding: '6px 6px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                    <option value="banner">Banner</option>
                  </select>
                  <select value={el.size ?? ''} onChange={e => set({ size: e.target.value ? Number(e.target.value) : null })}
                    title="Font size" style={{ padding: '6px 6px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
                    <option value="">Auto</option>
                    {[8, 10, 12, 14, 16, 18, 22, 28].map(n => <option key={n} value={n}>{n}px</option>)}
                  </select>
                  <select value={el.align ?? 'left'} onChange={e => set({ align: e.target.value as any })}
                    title="Alignment" style={{ padding: '6px 6px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button type="button" onClick={() => move(-1)} title="Move up" style={miniBtn}>↑</button>
                    <button type="button" onClick={() => move(1)} title="Move down" style={miniBtn}>↓</button>
                  </div>
                  <button type="button" title="Remove"
                    onClick={() => up({ custom_elements: els.filter((_, j) => j !== i) })}
                    style={{ ...miniBtn, color: '#dc2626' }}>✕</button>
                </div>
              )
            })}
            {(cfg.custom_elements ?? []).length < 12 && (
              <button type="button"
                onClick={() => up({ custom_elements: [...(cfg.custom_elements ?? []), { id: `el_${Date.now().toString(36)}`, text: '', region: 'body' as const, emphasis: 'normal' as const }] })}
                style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px dashed var(--color-border)', background: 'none', cursor: 'pointer', color: 'var(--color-text)' }}>
                + Add label text
              </button>
            )}
          </div>

          {err && <div style={errorBox}>{err}</div>}
          <button type="button" onClick={save} disabled={saving} style={{ ...primaryBtn, marginTop: 14 }}>
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save label settings'}
          </button>
        </div>

        {/* live preview */}
        <div style={{ flex: '0 0 auto' }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>Live preview — uses your unsaved edits</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
              Rx in package
              <select value={mockRxCount} onChange={e => setMockRxCount(Number(e.target.value))}
                style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={mockCopay} onChange={e => setMockCopay(e.target.checked)} />
              Mock copay (COD receipt)
            </label>
            <button type="button" onClick={() => setTestPrint(true)}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
              🖨 Print test label
            </button>
          </div>
          <div style={{ transform: cfg.size === 'letter' ? 'scale(0.42)' : 'scale(0.7)', transformOrigin: 'top left' }}>
            <ShippingLabel config={cfg} data={sample} />
          </div>
        </div>

        {/* D-SHIP-8: test-label print overlay — prints ONLY the label (visibility trick),
            full-size, with the current unsaved config + the mock data above. */}
        {testPrint && createPortal(
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, overflow: 'auto', padding: 24 }}>
            <style>{`
              @page { size: ${testPageSize}; margin: 0; }
              @media print {
                body * { visibility: hidden !important; }
                .test-label-print, .test-label-print * { visibility: visible !important; }
                .test-label-print { position: absolute !important; left: 0 !important; top: 0 !important; }
              }
            `}</style>
            <div style={{ background: '#fff', borderRadius: 10, padding: 16, maxWidth: 'fit-content', margin: '0 auto' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }} className="no-print">
                <button type="button" onClick={() => window.print()}
                  style={{ background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>
                  Print
                </button>
                <button type="button" onClick={() => setTestPrint(false)}
                  style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>
                  Close
                </button>
                <span style={{ fontSize: 12, color: '#475569', alignSelf: 'center' }}>TEST LABEL — mock data, not a real shipment</span>
              </div>
              <div className="test-label-print">
                <ShippingLabel config={cfg} data={sample} />
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  )
}

function Chk({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} /> {label}
    </label>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <span style={{ color: 'var(--color-text-muted)', minWidth: 110 }}>{label}</span>
      <span>{children}</span>
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, background: 'var(--color-surface)' }
const tag: React.CSSProperties = { padding: '1px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }
const badge: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, border: '1px solid var(--color-border)' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }
const mutedText: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 12 }
const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, boxSizing: 'border-box' }
const emptyBox: React.CSSProperties = { padding: 24, border: '1px dashed var(--color-border)', borderRadius: 8, textAlign: 'center', color: 'var(--color-text-muted)' }
const okBox: React.CSSProperties = { padding: 8, background: '#15803d0d', border: '1px solid #15803d33', borderRadius: 6, fontSize: 12, color: '#15803d', marginBottom: 8 }
const errorBox: React.CSSProperties = { padding: 8, background: '#dc26260d', border: '1px solid #dc262633', borderRadius: 6, fontSize: 12, color: '#dc2626' }
const miniBtn: React.CSSProperties = { border: '1px solid var(--color-border)', background: 'none', borderRadius: 4, fontSize: 11, padding: '2px 6px', cursor: 'pointer', color: 'var(--color-text)', lineHeight: 1.2 }
